import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { App, Button, Space, Spin, Typography, Upload } from 'antd';
import { useMemo, useState } from 'react';
import { uploadFileToOss } from '@/utils/oss';

export interface OssUploadedFileValue {
  file_name: string;
  file_url: string;
}

interface OssFileUploadFieldProps {
  value?: OssUploadedFileValue;
  onChange?: (value: OssUploadedFileValue) => void;
  accept?: string;
  dir?: string;
  sizeMB?: number;
  buttonText?: string;
}

const normalizeValue = (
  value?: OssUploadedFileValue,
): OssUploadedFileValue => ({
  file_name: String(value?.file_name || '').trim(),
  file_url: String(value?.file_url || '').trim(),
});

const toUploadFileList = (value?: OssUploadedFileValue): UploadFile[] => {
  const normalizedValue = normalizeValue(value);

  if (!normalizedValue.file_url) {
    return [];
  }

  return [
    {
      uid: normalizedValue.file_url,
      name: normalizedValue.file_name || 'uploaded-file',
      status: 'done',
      url: normalizedValue.file_url,
    },
  ];
};

const OssFileUploadField = ({
  value,
  onChange,
  accept = '.xlsx',
  dir = 'shop/imports/',
  sizeMB = 10,
  buttonText = '选择文件',
}: OssFileUploadFieldProps) => {
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);
  const normalizedValue = useMemo(() => normalizeValue(value), [value]);
  const fileList = useMemo(() => toUploadFileList(value), [value]);
  const allowedExtensions = useMemo(
    () =>
      accept
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean),
    [accept],
  );

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const fileName = String(file.name || '').toLowerCase();
    const extension = fileName.includes('.')
      ? `.${fileName.split('.').pop()}`
      : '';

    if (allowedExtensions.length && !allowedExtensions.includes(extension)) {
      message.warning(`仅支持上传 ${accept} 文件`);
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > sizeMB) {
      message.warning(`文件大小不能超过 ${sizeMB}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest: UploadProps['customRequest'] = async (options) => {
    try {
      setUploading(true);
      const file = options.file as File;
      const result = await uploadFileToOss(file, {
        dir,
      });
      const nextValue = {
        file_name: file.name,
        file_url: result.url,
      };
      onChange?.(nextValue);
      options.onSuccess?.(nextValue);
    } catch (error) {
      options.onError?.(error as Error);
      message.error((error as Error).message || '文件上传失败');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Spin spinning={uploading}>
        <Upload
          accept={accept}
          beforeUpload={beforeUpload}
          customRequest={customRequest}
          fileList={fileList}
          maxCount={1}
          onRemove={() => {
            onChange?.({
              file_name: '',
              file_url: '',
            });
            return true;
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {buttonText}
          </Button>
        </Upload>
      </Spin>

      <Typography.Text type="secondary">
        仅支持标准 {accept} 文件，单个文件不超过 {sizeMB}MB
      </Typography.Text>

      {normalizedValue.file_url ? (
        <Space size={4}>
          <PaperClipOutlined />
          <Typography.Text ellipsis style={{ maxWidth: 420 }}>
            {normalizedValue.file_name}
          </Typography.Text>
        </Space>
      ) : null}
    </Space>
  );
};

export default OssFileUploadField;
